import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { agentApi, mcpApi, openapiApi } from "../../../../api/client";
import { COLORS } from "../../../../theme/colors";
import { MATERIALS } from "../../../../theme/materials";

// Scoped bindings list — supersedes BindingsMatrix.
//
// Mental model: ONE list of integrations the family can use, each row
// carrying an explicit scope picker that says which family members
// (supervisor, experts, or any subset) the integration is bound to.
// The UI is supervisor-centric — authoring the family's wiring lives
// here. Per-expert tool-context isolation is preserved at runtime
// because saves write per-agent agent_integration_bindings rows
// according to scope.
//
// Atomic save via PUT /tree with expected_tree_version. The full
// desired set of bindings is sent each save; apply_tree soft-deletes
// rows not in the request.

const ROW_HEIGHT = 56;

// Compact summary string for the scope picker trigger.
// Exported for unit testing
export function scopeBadgeShort(scope, family) {
  if (!scope || scope.size === 0) return "Not bound";
  if (scope.size === family.length) return `All ${family.length} members`;
  if (scope.size === 1) {
    const id = [...scope][0];
    const m = family.find(f => f.id === id);
    return m?.name || "1 member";
  }
  // 2-3 → list names; 4+ → count
  if (scope.size <= 3) {
    const names = [...scope]
      .map(id => family.find(f => f.id === id)?.name)
      .filter(Boolean);
    return names.join(", ");
  }
  return `${scope.size} members`;
}

// Combined MCP + OpenAPI catalog with kind tag; enabled-first then
// alphabetical; free-text search on name / slug / server_id / description.
// Exported for unit testing
export function computeBindingRows(mcpCatalog, oapiCatalog, search) {
  const all = [
    ...(mcpCatalog || []).map(m => ({ ...m, _kind: "mcp" })),
    ...(oapiCatalog || []).map(o => ({ ...o, _kind: "openapi" })),
  ];
  all.sort((a, b) => {
    if ((a.enabled !== false) !== (b.enabled !== false)) {
      return (a.enabled !== false) ? -1 : 1;
    }
    return (a.name || "").localeCompare(b.name || "");
  });
  if (!search || !search.trim()) return all;
  const s = search.trim().toLowerCase();
  return all.filter(r =>
    (r.name || "").toLowerCase().includes(s) ||
    (r.slug || r.server_id || "").toLowerCase().includes(s) ||
    (r.description || "").toLowerCase().includes(s)
  );
}

// True if a single pending scope Set differs from its server Set.
// Exported for unit testing
export function scopeSetDiffers(pSet, sSet) {
  if (pSet.size !== sSet.size) return true;
  for (const x of pSet) if (!sSet.has(x)) return true;
  return false;
}

// True if ANY row's pending scope differs from the server scope.
// Exported for unit testing
export function bindingScopesDiffer(pending, serverScope) {
  const keys = new Set([...Object.keys(pending), ...Object.keys(serverScope)]);
  for (const k of keys) {
    if (scopeSetDiffers(pending[k] || new Set(), serverScope[k] || new Set())) return true;
  }
  return false;
}

// Build per-(kind,id) → Set<agent_id> from each family member's wiring rows.
// responses[i] aligns to family[i]; a null/failed response is skipped.
// Exported for unit testing
export function buildScopeFromWiringResponses(family, responses) {
  const scope = {};
  const init = (kind, id) => {
    const k = `${kind}:${id}`;
    if (!scope[k]) scope[k] = new Set();
    return scope[k];
  };
  family.forEach((m, i) => {
    const r = responses[i];
    if (!r?.data) return;
    for (const x of (r.data.mcp_servers || [])) {
      if (x.bound) init("mcp", x.id).add(m.id);
    }
    for (const x of (r.data.openapi_integrations || [])) {
      if (x.bound) init("openapi", x.id).add(m.id);
    }
  });
  return scope;
}

// Flatten the pending scope map into the PUT /tree payload shape.
// Exported for unit testing
export function buildTreeBindingsPayload({ pending, experts, supervisor }) {
  const mcp_server_bindings = [];
  const openapi_integration_bindings = [];
  Object.entries(pending).forEach(([k, agentSet]) => {
    const [kind, iid] = k.split(":");
    for (const aid of agentSet) {
      if (kind === "mcp") {
        mcp_server_bindings.push({ bound_to_agent_id: aid, mcp_server_id: iid });
      } else {
        openapi_integration_bindings.push({ bound_to_agent_id: aid, openapi_integration_id: iid });
      }
    }
  });
  return {
    expected_tree_version: supervisor?.tree_version ?? 0,
    experts: (experts || []).map(e => ({ agent_id: e.id })),
    integrations: { mcp_server_bindings, openapi_integration_bindings },
  };
}

function ScopePicker({ family, scope, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const buttonRef = React.useRef(null);
  // Anchored coordinates for the portaled dropdown. Computed each open.
  const [anchorRect, setAnchorRect] = useState(null);

  const toggle = useCallback((aid, next) => {
    const ns = new Set(scope);
    if (next) ns.add(aid); else ns.delete(aid);
    onChange(ns);
  }, [scope, onChange]);

  const setAll = useCallback(() => onChange(new Set(family.map(f => f.id))), [family, onChange]);
  const setNone = useCallback(() => onChange(new Set()), [onChange]);
  const setSupOnly = useCallback(() => {
    const sup = family.find(f => f.kind === "supervisor");
    onChange(sup ? new Set([sup.id]) : new Set());
  }, [family, onChange]);

  const handleOpen = () => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setAnchorRect({ left: r.left, top: r.bottom + 4, right: r.right, width: r.width });
    setOpen(true);
  };

  // Reposition on scroll/resize while open — portal lives at body root, so
  // anchor coords drift if user scrolls the panel under it.
  React.useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!buttonRef.current) return;
      const r = buttonRef.current.getBoundingClientRect();
      setAnchorRect({ left: r.left, top: r.bottom + 4, right: r.right, width: r.width });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Render the dropdown via a portal so it escapes both the
  // ScopedBindingsList container's `overflow: hidden` AND the inner row
  // scroller's `overflow: auto`. Without this, the popover is clipped and
  // appears blank or invisible. Positioned with fixed coords from the
  // button's bounding rect.
  const dropdown = open && anchorRect ? ReactDOM.createPortal(
    <>
      {/* Click-outside scrim */}
      <div onClick={() => setOpen(false)}
        style={{ position: "fixed", inset: 0, zIndex: 9999 }} />
      <div style={{
        position: "fixed",
        top: anchorRect.top,
        // Align dropdown's right edge with the button's right edge
        left: Math.max(8, anchorRect.right - 280),
        width: 280, maxHeight: 360, overflow: "auto", zIndex: 10000,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        boxShadow: MATERIALS.spatialShadow(),
        padding: 8,
      }}>
        <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
          <button type="button" onClick={setAll}
            style={{ flex: 1, padding: "4px 8px", fontSize: 10, fontWeight: 600,
              borderRadius: 4, cursor: "pointer", background: "transparent",
              border: `1px solid ${COLORS.border}`, color: COLORS.text }}>All</button>
          <button type="button" onClick={setSupOnly}
            style={{ flex: 1, padding: "4px 8px", fontSize: 10, fontWeight: 600,
              borderRadius: 4, cursor: "pointer", background: "transparent",
              border: `1px solid ${COLORS.border}`, color: COLORS.text }}>Supervisor only</button>
          <button type="button" onClick={setNone}
            style={{ flex: 1, padding: "4px 8px", fontSize: 10, fontWeight: 600,
              borderRadius: 4, cursor: "pointer", background: "transparent",
              border: `1px solid ${COLORS.border}`, color: COLORS.text }}>None</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {family.map(m => {
            const checked = scope.has(m.id);
            return (
              <label key={m.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "6px 8px", borderRadius: 6, cursor: "pointer",
                background: checked ? `${COLORS.green}10` : "transparent",
              }}>
                <input type="checkbox" checked={checked}
                  onChange={(e) => toggle(m.id, e.target.checked)}
                  style={{ accentColor: COLORS.green }} />
                <span style={{
                  fontSize: 9, fontWeight: 700, color: COLORS.textDim,
                  padding: "2px 6px", borderRadius: 4,
                  border: `1px solid ${COLORS.border}`,
                  fontFamily: "monospace",
                }}>{m.kind === "supervisor" ? "SUP" : "EXP"}</span>
                <span style={{ fontSize: 12, color: COLORS.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.name}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <div style={{ position: "relative", minWidth: 200 }}>
      <button type="button" ref={buttonRef}
        disabled={disabled}
        onClick={() => open ? setOpen(false) : handleOpen()}
        style={{
          padding: "6px 12px", fontSize: 11, fontWeight: 600,
          borderRadius: 6, cursor: disabled ? "default" : "pointer",
          background: scope.size > 0 ? `${COLORS.green}15` : "transparent",
          border: `1px solid ${scope.size > 0 ? `${COLORS.green}55` : COLORS.border}`,
          color: scope.size > 0 ? COLORS.green : COLORS.textMuted,
          width: "100%", textAlign: "left",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {scopeBadgeShort(scope, family)}
        </span>
        <span style={{ marginLeft: 8, color: COLORS.textDim }}>{open ? "▴" : "▾"}</span>
      </button>
      {dropdown}
    </div>
  );
}

export default function ScopedBindingsList({
  supervisor,           // { id, name, tree_version }
  experts = [],         // [{ id, name }]
  canEdit,
  onSaved,              // (newTreeVersion) => void
}) {
  const family = useMemo(() => {
    if (!supervisor?.id) return [];
    return [
      { id: supervisor.id, name: supervisor.name, kind: "supervisor" },
      ...experts.map(e => ({ id: e.id, name: e.name, kind: "expert" })),
    ];
  }, [supervisor, experts]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");

  // Tenant catalogs (rows of the list).
  const [mcpCatalog, setMcpCatalog]   = useState([]);
  const [oapiCatalog, setOapiCatalog] = useState([]);

  // Server scope: { [`${kind}:${integration_id}`]: Set<agent_id> }
  const [serverScope, setServerScope] = useState({});
  // Pending scope (mutated by user). Same shape.
  const [pending, setPending] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const refresh = useCallback(async () => {
    if (family.length === 0) return;
    setLoading(true); setErr(null); setSaveError(null);
    try {
      // Sequential getWiring (NOT Promise.all). Five parallel wiring
      // calls were exhausting the CNPG connection pool — one call took
      // 120s before recovering, and other calls failed with
      // "connection was closed in the middle of operation". The
      // wiring endpoint touches mcp_servers + openapi_integrations +
      // agent_integration_bindings + workflows + tenant_settings —
      // heavy enough that 5 concurrent calls × multiple queries
      // overwhelmed the pool. Sequential awaits = ~250ms × N members
      // (acceptable: ~1.25s for a 5-member family).
      const responses = [];
      for (const m of family) {
        try {
          responses.push(await agentApi.getWiring(m.id));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`getWiring(${m.id}) failed:`, e);
          responses.push(null);
        }
      }
      const supWire = responses[0];
      if (!supWire?.data) throw new Error("Failed to load supervisor wiring");

      const mcps  = supWire.data.mcp_servers           || [];
      const oapis = supWire.data.openapi_integrations  || [];
      setMcpCatalog(mcps);
      setOapiCatalog(oapis);

      // Build per-(kind,id) → Set<agent_id> from each agent's bound rows.
      const scope = buildScopeFromWiringResponses(family, responses);
      setServerScope(scope);

      // Clone for pending.
      const clone = {};
      Object.entries(scope).forEach(([k, v]) => { clone[k] = new Set(v); });
      setPending(clone);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [family]);

  useEffect(() => {
    refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisor?.id, JSON.stringify(experts.map(e => e.id))]);

  // Combined catalog with kind tag for rendering.
  const rows = useMemo(
    () => computeBindingRows(mcpCatalog, oapiCatalog, search),
    [mcpCatalog, oapiCatalog, search]
  );

  const dirty = useMemo(
    () => bindingScopesDiffer(pending, serverScope),
    [pending, serverScope]
  );

  const setRowScope = useCallback((kind, id, newScope) => {
    setPending(prev => ({ ...prev, [`${kind}:${id}`]: new Set(newScope) }));
  }, []);

  const handleDiscard = useCallback(() => {
    const clone = {};
    Object.entries(serverScope).forEach(([k, v]) => { clone[k] = new Set(v); });
    setPending(clone);
    setSaveError(null);
  }, [serverScope]);

  const handleSave = useCallback(async () => {
    if (!dirty) return;
    setSaving(true); setSaveError(null);
    try {
      // canvas_node_id intentionally omitted — column is VARCHAR(64) and
      // a `${kind}-${aid}-${iid}` shape exceeds it. The (agent_id, kind,
      // integration_id) tuple is the actual key for apply_tree's diff
      // logic, so the field is informational metadata only — fine to
      // leave null for non-canvas saves.
      const payload = buildTreeBindingsPayload({ pending, experts, supervisor });
      const res = await agentApi.putTree(supervisor.id, payload);
      const newTV = res?.data?.supervisor?.tree_version;
      if (typeof newTV === "number" && onSaved) onSaved(newTV);
      await refresh();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg.includes("412") || msg.toLowerCase().includes("stale")) {
        setSaveError(
          "Tree changed in another tab or in the Builder. Click Discard to " +
          "reload the latest, then re-apply your changes."
        );
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [dirty, pending, experts, supervisor, refresh, onSaved]);

  // Hide entirely for regular agents (single-row family).
  if (family.length <= 1) return null;

  const isCellDirty = (k) =>
    scopeSetDiffers(pending[k] || new Set(), serverScope[k] || new Set());

  return (
    <div style={{
      borderRadius: 12,
      border: `1px solid ${COLORS.border}`,
      background: COLORS.surface,
      boxShadow: MATERIALS.spatialShadow(),
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "12px 16px",
        borderBottom: `1px solid ${COLORS.border}`,
        gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLORS.textDim,
            textTransform: "uppercase", letterSpacing: "0.06em",
          }}>
            Family wiring · {rows.length} integration{rows.length !== 1 ? "s" : ""}
            {" · "}{family.length} member{family.length !== 1 ? "s" : ""}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
            One integration per row. Pick which family members each is bound
            to via the Scope dropdown — supervisor only, all experts, or
            specific experts. Saves are atomic.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="text" value={search} placeholder="Search…"
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "6px 10px", fontSize: 12,
              borderRadius: 6, border: `1px solid ${COLORS.border}`,
              background: COLORS.bg, color: COLORS.text, width: 180,
            }} />
          {savedFlash && (
            <span style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              background: `${COLORS.green}25`, color: COLORS.green,
              border: `1px solid ${COLORS.green}66`,
            }}>Saved</span>
          )}
          {dirty && !saving && (
            <button type="button" onClick={handleDiscard}
              style={{
                padding: "4px 10px", fontSize: 11, fontWeight: 600,
                borderRadius: 6, cursor: "pointer",
                background: "transparent",
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted,
              }}>Discard</button>
          )}
          <button type="button"
            disabled={!dirty || saving || !canEdit}
            onClick={handleSave}
            title={!canEdit ? "Read-only — your role can't bind integrations" : undefined}
            style={{
              padding: "4px 14px", fontSize: 11, fontWeight: 700,
              borderRadius: 6,
              cursor: (!dirty || saving || !canEdit) ? "default" : "pointer",
              background: (dirty && !saving && canEdit) ? COLORS.accent : "transparent",
              border: `1px solid ${(dirty && !saving && canEdit) ? COLORS.accent : COLORS.border}`,
              color: (dirty && !saving && canEdit) ? "#fff" : COLORS.textDim,
              opacity: saving ? 0.55 : 1,
            }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      {saveError && (
        <div style={{
          padding: "10px 16px", fontSize: 12,
          background: `${COLORS.pink}1A`, color: COLORS.pink,
          borderBottom: `1px solid ${COLORS.pink}66`,
        }}>{saveError}</div>
      )}

      {loading && (
        <div style={{ padding: 16, fontSize: 12, color: COLORS.textDim }}>
          Loading bindings…
        </div>
      )}
      {err && !loading && (
        <div style={{ padding: 16, fontSize: 12, color: COLORS.pink }}>
          Failed to load: {err}
        </div>
      )}
      {!loading && !err && rows.length === 0 && (
        <div style={{ padding: 16, fontSize: 12, color: COLORS.textDim }}>
          {search.trim()
            ? `No integrations match "${search}".`
            : "No integrations registered in this tenant yet. Use Admin → Integrations to register one."}
        </div>
      )}

      {!loading && !err && rows.length > 0 && (
        <div style={{ maxHeight: 520, overflow: "auto" }}>
          {rows.map((r) => {
            const k = `${r._kind}:${r.id}`;
            const scope = pending[k] || new Set();
            const cellDirty = isCellDirty(k);
            return (
              <div key={k} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 16px",
                borderBottom: `1px solid ${COLORS.border}55`,
                background: cellDirty ? "#f59e0b15" : "transparent",
                minHeight: ROW_HEIGHT,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      {r.name}
                    </span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: COLORS.textDim,
                      padding: "2px 6px", borderRadius: 4,
                      border: `1px solid ${COLORS.border}`,
                      fontFamily: "monospace",
                    }}>{r._kind === "mcp" ? "MCP" : "OAPI"}</span>
                    {r.enabled === false && (
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>(disabled)</span>
                    )}
                  </div>
                  {(r.description || r.base_url || r.slug || r.server_id) && (
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.description || r.base_url || r.slug || r.server_id}
                    </div>
                  )}
                </div>
                <ScopePicker
                  family={family}
                  scope={scope}
                  onChange={(s) => setRowScope(r._kind, r.id, s)}
                  disabled={!canEdit || saving}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
